// Gift With Purchase System - Tag-Based Approach
// ================================================
//
// ADMIN SETUP INSTRUCTIONS:
// 1. Add tags to products that should trigger gifts
// 2. Tag format: "gwp-gift-{variant_id}"
//    Example: "gwp-gift-123456789" (where 123456789 is the gift variant ID)
// 3. Only products with subscription selling plans will trigger gifts
// 4. Each product can have one gift tag
// 5. If multiple qualifying products have different gifts, the first one found will be used
//
// HOW IT WORKS:
// - Monitors all cart changes via fetch/XHR patching
// - When cart changes, checks for qualifying subscription products
// - Fetches product data to check for gift tags
// - Adds/removes gifts based on qualifying products
// - Works with any cart system (ReCharge, Rebuy, etc.)

(function() {
  'use strict';

  // Configuration
  const config = {
    giftProperties: {
      '_gwp_source': 'gwp_system',
      '_gwp_type': 'welcome_kit',
      '_gwp_discount_title': 'Free Gift',
      '_gwp_discount_type': 'percentage',
      '_gwp_discount_value': '100'
    },
    settings: { 
      debugMode: false,  // Disabled for production
      enabled: true,
      debounceMs: 200,
      postWriteDelayMs: 200
    }
  };

  // Cache for product data to avoid repeated API calls
  const productCache = new Map();
  
  // Flag to prevent multiple simultaneous writes
  let isWriting = false;
  
  // Flag to prevent infinite loops from our own cart calls
  let isOurCartCall = false;
  
  // Circuit breaker to prevent infinite loops
  let isCircuitBreakerOpen = false;
  let circuitBreakerResetTime = 0;
  
  // Monitoring and performance tracking
  let cartCallCount = 0;
  let giftSystemCalls = 0;
  let productApiCalls = 0;
  let lastCartState = null;
  let lastCartItemCount = 0;
  let lastCartTotal = 0;
  let performanceStartTime = Date.now();
  
  // Debounce utility
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Sleep utility
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Check if cart has meaningful changes (items added/removed)
  function hasCartChanged(newCart) {
    if (!lastCartState) {
      // First time - store initial state
      lastCartState = {
        itemCount: newCart.item_count,
        total: newCart.total_price,
        items: newCart.items.map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties,
          selling_plan: item.selling_plan_allocation?.selling_plan?.id || null
        }))
      };
      return false; // Don't trigger on first load
    }

    const currentState = {
      itemCount: newCart.item_count,
      total: newCart.total_price,
      items: newCart.items.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties,
        selling_plan: item.selling_plan_allocation?.selling_plan?.id || null
      }))
    };

    // Check if item count changed
    if (currentState.itemCount !== lastCartState.itemCount) {
      lastCartState = currentState;
      return true;
    }

    // Check if any item quantities changed
    for (const currentItem of currentState.items) {
      const lastItem = lastCartState.items.find(item => item.id === currentItem.id);
      if (!lastItem || lastItem.quantity !== currentItem.quantity) {
        lastCartState = currentState;
        return true;
      }
    }

    // Check if any items were removed
    for (const lastItem of lastCartState.items) {
      const currentItem = currentState.items.find(item => item.id === lastItem.id);
      if (!currentItem) {
        lastCartState = currentState;
        return true;
      }
    }

    // Check if selling plans changed (subscription <-> one-time)
    for (const currentItem of currentState.items) {
      const lastItem = lastCartState.items.find(item => item.id === currentItem.id);
      if (lastItem && lastItem.selling_plan !== currentItem.selling_plan) {
        lastCartState = currentState;
        return true;
      }
    }

    return false;
  }

  // Add gift to cart
  async function addGiftToCart(giftVariantId) {
    if (isWriting) {
      return;
    }

    try {
      isWriting = true;
      giftSystemCalls++;
      
      const giftData = {
        id: giftVariantId,
        quantity: 1,
        properties: config.giftProperties
      };
      
      // Set flag to prevent infinite loop
      isOurCartCall = true;
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(giftData)
      });
      
      // Clear flag
      isOurCartCall = false;
      
      if (response.ok) {
        // Wait for cart to update
        await sleep(config.settings.postWriteDelayMs);
      } else {
        const errorData = await response.json();
        console.error('❌ GWP: Failed to add gift:', errorData);
        
        // If we get rate limited, open circuit breaker
        if (errorData.status === 'too_many_requests') {
          isCircuitBreakerOpen = true;
          circuitBreakerResetTime = Date.now() + (10 * 60 * 1000); // 10 minutes
          console.warn('🚫 GWP: Rate limited - circuit breaker open for 10 minutes');
        }
      }
    } catch (error) {
      console.error('❌ GWP: Error adding gift:', error);
    } finally {
      isWriting = false;
    }
  }

  // Update cart count in header
  function updateCartCount() {
    const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .header__cart-count');
    
    cartCountElements.forEach(element => {
      if (element) {
        // Get current cart count from the cart data
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
            element.textContent = totalItems;
            
            // Also update any aria-label attributes
            const parent = element.closest('a, button');
            if (parent) {
              parent.setAttribute('aria-label', `Cart with ${totalItems} items`);
            }
          })
          .catch(error => {
            console.warn('Failed to update cart count:', error);
          });
      }
    });
  }

  // Remove gift from cart
  async function removeGiftFromCart() {
    if (isWriting) {
      return;
    }

    try {
      isWriting = true;
      giftSystemCalls++;
      
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      const giftItems = cart.items.filter(item =>
        item.properties &&
        item.properties._gwp_source === 'gwp_system'
      );
      
      for (const gift of giftItems) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0
          })
        });
        
        if (!response.ok) {
          console.error('❌ GWP: Failed to remove gift:', response.status);
        }
      }
      
      // Wait for cart to update
      await sleep(config.settings.postWriteDelayMs);
      
    } catch (error) {
      console.error('❌ GWP: Error removing gift:', error);
    } finally {
      isWriting = false;
    }
  }

  // Get gift variant ID from product tags
  async function getGiftVariantIdFromProduct(productHandle) {
    // Check cache first
    if (productCache.has(productHandle)) {
      return productCache.get(productHandle);
    }

    try {
      productApiCalls++;
      
      // Fetch the product directly by handle
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        productCache.set(productHandle, null);
        return null;
      }
      
      const product = await response.json();
      
      // Look for gift tag
      const giftTag = product.tags.find(tag => tag.startsWith('gwp-gift-'));
      
      if (giftTag) {
        const giftVariantId = giftTag.replace('gwp-gift-', '');
        
        // Cache the result
        productCache.set(productHandle, giftVariantId);
        
        return giftVariantId;
      }
      
      // Cache negative result too
      productCache.set(productHandle, null);
      return null;
      
    } catch (error) {
      // Cache negative result
      productCache.set(productHandle, null);
      return null;
    }
  }

  // Main gift logic - uses cart data from event
  async function processGiftLogic(cartData = null) {
    // Check circuit breaker
    if (isCircuitBreakerOpen) {
      if (Date.now() < circuitBreakerResetTime) {
        return;
      } else {
        // Reset circuit breaker
        isCircuitBreakerOpen = false;
      }
    }
    
    if (isWriting) {
      return;
    }

    try {
      // Use provided cart data or fetch if not available
      let cart;
      if (cartData) {
        cart = cartData;
      } else {
        // Fallback: fetch cart data (should rarely happen)
        const cartResponse = await fetch('/cart.js');
        cart = await cartResponse.json();
      }

      // Update cart count for any cart change
      updateCartCount();

      // Find qualifying products (subscriptions, not gifts)
      const qualifyingProducts = cart.items.filter(item => {
        // Skip gift items
        if (item.properties && item.properties._gwp_source === 'gwp_system') {
          return false;
        }
        
        // Check if this item has a subscription
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      });

      // Check if gift is in cart
      const hasGift = cart.items.some(item =>
        item.properties &&
        item.properties._gwp_source === 'gwp_system'
      );

      // If no qualifying products, remove any existing gifts
      if (qualifyingProducts.length === 0) {
        if (hasGift) {
          await removeGiftFromCart();
        }
        return;
      }

      // Get unique product handles from qualifying products
      const productHandles = [...new Set(qualifyingProducts.map(item => item.handle))];
      
      // Check each product for gift tags
      let giftVariantId = null;
      for (const productHandle of productHandles) {
        const foundGiftId = await getGiftVariantIdFromProduct(productHandle);
        if (foundGiftId) {
          giftVariantId = foundGiftId;
          break; // Use first gift found
        }
      }

      // Add gift if we have qualifying products and no gift
      if (qualifyingProducts.length > 0 && !hasGift && giftVariantId) {
        await addGiftToCart(giftVariantId);
      }
      
    } catch (error) {
      console.error('❌ GWP: Error processing gift logic:', error);
    }
  }

  // Debounced gift logic
  const debouncedGiftLogic = debounce((cartData) => {
    processGiftLogic(cartData);
  }, config.settings.debounceMs);

  // Setup cart change detection
  function setupCartChangeDetection() {
    // Store original functions if not already stored
    if (!window.originalFetch) {
      window.originalFetch = window.fetch;
    }
    if (!window.originalXMLHttpRequestOpen) {
      window.originalXMLHttpRequestOpen = XMLHttpRequest.prototype.open;
    }
    
    // Patch fetch
    const originalFetch = window.originalFetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if this is a cart API call
      if (typeof args[0] === 'string' && args[0].includes('/cart')) {
        // Skip if this is our own cart call to prevent infinite loop
        if (isOurCartCall) {
          return response;
        }
        
        try {
          cartCallCount++;
          
          // Get caller info for monitoring
          const stack = new Error().stack;
          const stackLines = stack.split('\n');
          const caller = stackLines[2] || 'unknown';
          
          // Check if this is from a known problematic source
          const isFromElevar = caller.includes('dl-app-embed-block.js');
          const isFromShopEvents = caller.includes('shop_events_listener');
          const isFromPrequal = caller.includes('prequal-widget');
          const isFromOnward = caller.includes('onward.js');
          const isFromGiftSystem = caller.includes('gift-with-purchase.js');
          const isFromRecharge = caller.includes('recharge') || caller.includes('ReCharge');
          
          // Log only problematic sources
          if (isFromElevar || isFromShopEvents || isFromPrequal || isFromOnward) {
            console.warn(`🚨 GWP: External app cart call #${cartCallCount}:`, {
              source: isFromElevar ? 'Elevar' : isFromShopEvents ? 'ShopEvents' : isFromPrequal ? 'Prequal' : 'Onward',
              url: args[0]
            });
          }
          
          const cloned = response.clone();
          const cart = await cloned.json();
          
          // Check if this is a rate limit response
          if (cart.status === 'too_many_requests') {
            console.warn('🚫 GWP: Rate limit detected - opening circuit breaker');
            isCircuitBreakerOpen = true;
            circuitBreakerResetTime = Date.now() + (10 * 60 * 1000); // 10 minutes
            return response;
          }
          
          // Only dispatch event if cart actually changed
          if (hasCartChanged(cart)) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          }
        } catch (error) {
          // Ignore errors (might be non-JSON response)
        }
      }
      
      return response;
    };

    // Patch XMLHttpRequest for older cart systems
    const originalOpen = window.originalXMLHttpRequestOpen;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.addEventListener('load', () => {
        if (typeof url === 'string' && url.includes('/cart')) {
          try {
            const cart = JSON.parse(this.responseText);
            
            // Only dispatch event if cart actually changed
            if (hasCartChanged(cart)) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            }
          } catch (error) {
            // Ignore errors (might be non-JSON response)
          }
        }
      });
      return originalOpen.call(this, method, url, ...rest);
    };
  }

  // Setup cart event listener
  function setupCartEventListener() {
    document.addEventListener('cart:updated', (e) => {
      // Pass the cart data from the event to avoid another API call
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for ReCharge specific events
    document.addEventListener('recharge:cart:updated', (e) => {
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for any custom cart events that might be dispatched by ReCharge
    document.addEventListener('cart:change', (e) => {
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for ReCharge subscription changes
    document.addEventListener('recharge:subscription:changed', (e) => {
      // Force a cart check after a short delay to allow ReCharge to update
      setTimeout(() => {
        debouncedGiftLogic();
      }, 1000);
    });
  }

  // Initialize the gift system
  function initGiftSystem() {
    if (!config.settings.enabled) {
      return;
    }

    setupCartChangeDetection();
    setupCartEventListener();

    // Initial check on page load
    setTimeout(() => {
      debouncedGiftLogic();
      // Update cart count on page load
      updateCartCount();
    }, 1000);
  }

  // Start the system when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftSystem);
  } else {
    initGiftSystem();
  }

  // Make functions globally accessible for debugging
  window.giftSystem = {
    processGiftLogic,
    addGiftToCart,
    removeGiftFromCart,
    getGiftVariantIdFromProduct,
    config,
    productCache,
    getCartCallCount: () => cartCallCount,
    getGiftSystemCalls: () => giftSystemCalls,
    getProductApiCalls: () => productApiCalls,
    resetCounters: () => { 
      cartCallCount = 0; 
      giftSystemCalls = 0; 
      productApiCalls = 0; 
      performanceStartTime = Date.now();
    },
    openCircuitBreaker: () => {
      isCircuitBreakerOpen = true;
      circuitBreakerResetTime = Date.now() + (5 * 60 * 1000);
      console.log('🚫 GWP: Circuit breaker opened manually for 5 minutes');
    },
    closeCircuitBreaker: () => {
      isCircuitBreakerOpen = false;
      console.log('✅ GWP: Circuit breaker closed manually');
    },
    getCircuitBreakerStatus: () => ({
      isOpen: isCircuitBreakerOpen,
      resetTime: circuitBreakerResetTime,
      timeUntilReset: Math.max(0, circuitBreakerResetTime - Date.now())
    })
  };

  // Circuit breaker test function
  window.testCircuitBreaker = function() {
    const status = window.giftSystem.getCircuitBreakerStatus();
    console.log('🔌 Circuit Breaker Status:', status);
    if (status.isOpen) {
      console.log(`⏰ Time until reset: ${Math.round(status.timeUntilReset / 1000)} seconds`);
    }
  };

  // Comprehensive monitoring function
  window.monitorGiftSystem = function() {
    const uptime = Math.round((Date.now() - performanceStartTime) / 1000);
    const cartCalls = cartCallCount;
    const giftCalls = giftSystemCalls;
    const productCalls = productApiCalls;
    const cacheSize = productCache.size;
    
    console.log('📊 GIFT SYSTEM MONITORING');
    console.log('========================');
    console.log(`⏱️  Uptime: ${uptime} seconds`);
    console.log(`🛒 Cart calls intercepted: ${cartCalls}`);
    console.log(`🎁 Gift system calls: ${giftCalls}`);
    console.log(`📦 Product API calls: ${productCalls}`);
    console.log(`💾 Cache size: ${cacheSize} products`);
    console.log(`🔌 Circuit breaker: ${isCircuitBreakerOpen ? 'OPEN' : 'CLOSED'}`);
    console.log(`✍️  Currently writing: ${isWriting}`);
    console.log('');
    
    // Performance analysis
    if (uptime > 0) {
      const cartCallsPerMinute = Math.round((cartCalls / uptime) * 60);
      const giftCallsPerMinute = Math.round((giftCalls / uptime) * 60);
      const productCallsPerMinute = Math.round((productCalls / uptime) * 60);
      
      console.log('📈 PERFORMANCE METRICS');
      console.log('=====================');
      console.log(`🛒 Cart calls/minute: ${cartCallsPerMinute}`);
      console.log(`🎁 Gift calls/minute: ${giftCallsPerMinute}`);
      console.log(`📦 Product calls/minute: ${productCallsPerMinute}`);
      console.log('');
    }
    
    // Warnings
    if (cartCalls > 50) {
      console.warn('⚠️  HIGH CART CALL VOLUME - Check for external app issues');
    }
    if (productCalls > 20) {
      console.warn('⚠️  HIGH PRODUCT API USAGE - Consider caching improvements');
    }
    if (isCircuitBreakerOpen) {
      console.warn('⚠️  CIRCUIT BREAKER OPEN - Gift system disabled');
    }
    
    console.log('💡 Run window.giftSystem.resetCounters() to reset all counters');
  };

  // Reset cart state tracking
  window.resetCartState = function() {
    lastCartState = null;
    lastCartItemCount = 0;
    lastCartTotal = 0;
    console.log('🔄 GWP: Cart state tracking reset');
  };

  // Emergency stop function
  window.emergencyStop = function() {
    isCircuitBreakerOpen = true;
    circuitBreakerResetTime = Date.now() + (15 * 60 * 1000); // 15 minutes
    console.log('🚨 GWP: EMERGENCY STOP ACTIVATED');
    console.log('🚨 GWP: All gift logic disabled for 15 minutes');
    console.log('🚨 GWP: Reset time:', new Date(circuitBreakerResetTime).toLocaleTimeString());
  };

  // Completely disable cart change detection
  window.disableCartDetection = function() {
    // Restore original fetch
    if (window.originalFetch) {
      window.fetch = window.originalFetch;
    }
    
    // Restore original XMLHttpRequest
    if (window.originalXMLHttpRequestOpen) {
      XMLHttpRequest.prototype.open = window.originalXMLHttpRequestOpen;
    }
    
    console.log('🚫 GWP: Cart change detection disabled');
  };

  // Re-enable cart change detection
  window.enableCartDetection = function() {
    setupCartChangeDetection();
    console.log('✅ GWP: Cart change detection re-enabled');
  };

  // Test function
  window.testGiftSystem = async function() {
    console.log('🧪 GWP: Testing gift system...');
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Check for qualifying products
      const qualifyingProducts = cart.items.filter(item => {
        if (item.properties && item.properties._gwp_source === 'gwp_system') {
          return false;
        }
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      });
      
      console.log('🧪 GWP: Qualifying products found:', qualifyingProducts.length);
      
      if (qualifyingProducts.length > 0) {
        await processGiftLogic();
      }
      
    } catch (error) {
      console.error('🧪 GWP: Test error:', error);
    }
  };

  // ReCharge debugging function
  window.debugRechargeCart = async function() {
    console.log('🔍 GWP: ReCharge Cart Debugging');
    console.log('==============================');
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Check for qualifying products
      const qualifyingProducts = cart.items.filter(item => {
        if (item.properties && item.properties._gwp_source === 'gwp_system') {
          return false;
        }
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      });
      
      // Check for gifts
      const giftItems = cart.items.filter(item => 
        item.properties && item.properties._gwp_source === 'gwp_system'
      );
      
      console.log('📊 Cart State:', {
        total_items: cart.items.length,
        qualifying_products: qualifyingProducts.length,
        gift_items: giftItems.length,
        items: cart.items.map(item => ({
          title: item.title,
          selling_plan: item.selling_plan_allocation?.selling_plan?.name || 'No plan',
          is_gift: !!(item.properties && item.properties._gwp_source === 'gwp_system')
        }))
      });
      
    } catch (error) {
      console.error('❌ GWP: ReCharge debug error:', error);
    }
  };

  // Force cart check (useful for ReCharge debugging)
  window.forceCartCheck = function() {
    console.log('🔄 GWP: Forcing cart check...');
    debouncedGiftLogic();
  };

  // Manually update cart count
  window.updateCartCount = function() {
    console.log('🛒 GWP: Manually updating cart count...');
    updateCartCount();
  };

})();